import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as SettingsIcon } from "src/images/settings.svg";
import { Button } from "src/app/components/Button/Button";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ExtensionTypeEnum, Identity, TaskType } from "src/types";
import { ReactComponent as SendIcon } from "src/images/send.svg";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import { ITask } from "src/redux/task/taskReducer";
import { useCheckFeatureAccess } from "src/app/subscription/subscriptionFunctions";
import { SubscriptionEntitlements } from "src/app/subscription/subscriptionUtils";
import {
  setCreateSingleSplitModalOpen,
  setCreatorMessagePaneOpen,
} from "src/redux/project/projectActions";
import { setPlanUpgradeNeededModalContext } from "src/redux/subscription/subscriptionActions";
import { RootState } from "src/redux/reducers";
import { ReactComponent as CreatePaymentsIcon } from "src/images/payment.svg";
import { API_URLS } from "src/utils/API_URLS";
import { wsAxiosPost } from "src/helpers/wsAxios";
import { showToast } from "src/app/methods/showToast";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import useCheckWorkspacePermission from "../../../../../hooks/useCheckWorkspacePermission";

interface MoreSettingsDropdownProps {
  taskType: TaskType;
  selectedTasks: ITask[];
  onClearSelected: () => void;
  tasksLoading: boolean;
}

export default function MoreSettingsDropdown({
  taskType,
  selectedTasks,
  onClearSelected,
  tasksLoading,
}: MoreSettingsDropdownProps) {
  const wsExtensions = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.wsExtensions,
  );

  const projectId = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.projectId,
  );

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const dispatch = useDispatch();

  const maxCreatorsForSendingMessage =
    useSelector(
      (state: RootState) =>
        (state.mainReducer.identity as Partial<Identity>)
          .maxCreatorsForSendingMessage,
    ) ?? 100;

  const hasFeatureAccess = useCheckFeatureAccess(
    SubscriptionEntitlements.WsMessengerExtension,
  );

  const hasTaskManagementPermission =
    useCheckProjectPermission("TASK_MANAGEMENT");

  const hasAccessToWsMessengerExtension = useCheckWorkspacePermission(
    "SEND_WS_MESSENGER_MESSAGE",
  );

  if (taskType !== TaskType.Creator && taskType !== TaskType.Publication) {
    return null;
  }

  const openCreatorMessagePane = () => {
    if (hasFeatureAccess) {
      dispatch(setCreatorMessagePaneOpen(true));
    } else {
      dispatch(
        setPlanUpgradeNeededModalContext(
          SubscriptionEntitlements.WsMessengerExtension,
        ),
      );
    }
  };

  const hasCreateSingleSplitPaymentsExtension = wsExtensions
    ? wsExtensions.find(
        (extenson) =>
          extenson.type === ExtensionTypeEnum.CreateSingleAndSplitPayments,
      ) !== undefined
    : false;

  const renderSendMessageButton = () => {
    if (taskType === TaskType.Creator && hasAccessToWsMessengerExtension) {
      return (
        <TooltipPortal
          content={
            <span>{`Cannot send message for more than ${maxCreatorsForSendingMessage} creators`}</span>
          }
          contentHidden={selectedTasks.length <= maxCreatorsForSendingMessage}
          up
        >
          <DropdownMenuItem
            onClick={openCreatorMessagePane}
            data-qa="openCreatorMessagePane"
            disabled={selectedTasks.length > maxCreatorsForSendingMessage}
          >
            <SendIcon />
            <IDHFormattedMessage
              id="ws_send_message"
              defaultMessage="Send Message"
            />
          </DropdownMenuItem>
        </TooltipPortal>
      );
    }
    return null;
  };

  const renderCreatePaymentsButton = () => {
    if (hasTaskManagementPermission && !hasCreateSingleSplitPaymentsExtension) {
      return (
        <DropdownMenuItem
          disabled={!selectedTasks.length || tasksLoading}
          data-qa="createPaymentsForItems"
          onClick={createPaymentsForItems}
        >
          <CreatePaymentsIcon />
          <IDHFormattedMessage
            id="ws_create_payments"
            defaultMessage="Create payments"
          />
        </DropdownMenuItem>
      );
    }
    if (hasCreateSingleSplitPaymentsExtension && hasTaskManagementPermission) {
      return (
        <TooltipPortal
          content={
            <IDHFormattedMessage
              id="ws_cannot_create_payments"
              defaultMessage="Cannot create payments for more than 1 creator"
            />
          }
          contentHidden={selectedTasks.length === 1}
          up
        >
          <DropdownMenuItem
            onClick={openCreateSingleSplitPaymentsModal}
            data-qa="createFromSingleAndSplitPaymentsExtension"
            disabled={selectedTasks.length > 1 || tasksLoading}
          >
            <CreatePaymentsIcon />
            <IDHFormattedMessage
              id="ws_create_payments"
              defaultMessage="Create payments"
            />
          </DropdownMenuItem>
        </TooltipPortal>
      );
    }
    return null;
  };

  const openCreateSingleSplitPaymentsModal = () => {
    dispatch(setCreateSingleSplitModalOpen(true));
  };

  const createPaymentsForItems = () => {
    if (!projectId) return;
    const uuids = selectedTasks.map((selectedTask) => selectedTask.taskId);
    wsAxiosPost(
      API_URLS.createPaymentsToSelectedTasks,
      {
        wsTaskUuids: uuids,
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: projectId,
        wsTaskType: taskType,
      },
      undefined,
      () => {},
      () => {
        showToast(
          "success",
          <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
          <IDHFormattedMessage
            id="ws_create_payment_for_selected_task"
            defaultMessage="Payment for selected item(s) will be add soon"
          />,
        );
        onClearSelected();
      },
      () => {},
    );
  };
  const hasSendMessageOption =
    taskType === TaskType.Creator && hasAccessToWsMessengerExtension;

  if (!hasSendMessageOption && !hasTaskManagementPermission) {
    return null;
  }

  return (
    <Dropdown toBottom>
      {(isOpen, setIsOpen) => (
        <>
          <Button
            size="square-small"
            variant="light-grey"
            onClick={() => setIsOpen((prev) => !prev)}
            data-qa="more-settings-dropdown-button"
          >
            <SettingsIcon />
          </Button>
          <DropdownMenu isOpen={isOpen}>
            {renderSendMessageButton()}
            {renderCreatePaymentsButton()}
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
}
